import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { toast } from "sonner";
import { 
  Phone, 
  FileText, 
  ShoppingCart,
  CheckCircle,
  AlertCircle,
  Clock,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  MapPin,
  Calendar,
  ClipboardList,
  Settings,
  RefreshCw,
  X,
  Filter,
  CreditCard,
  MessageSquare,
  Send,
  Repeat,
  Pause,
  Play,
  Mail,
  Mic,
  Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPICard = ({ title, value, subtitle, icon: Icon, color = "primary" }) => (
  <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] sm:text-xs uppercase tracking-wider text-white/70 font-medium">{title}</p>
          <p className="text-2xl sm:text-3xl font-bold text-white mt-1 font-['Chivo']">{value}</p>
          {subtitle && <p className="text-xs sm:text-sm text-white/50 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
          color === "primary" ? "bg-[#f4cc1e]/20" : 
          color === "success" ? "bg-green-500/20" :
          color === "danger" ? "bg-orange-500/20" : "bg-[#1a3a6a]"
        }`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${
            color === "primary" ? "text-[#f4cc1e]" : 
            color === "success" ? "text-green-400" :
            color === "danger" ? "text-orange-400" : "text-gray-400"
          }`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const REQUEST_TYPE_OPTIONS = [
  { value: "criteria", label: "Criteria Changes", placeholder: "Describe the criteria changes you need (roof age, denials, financing, etc.)..." },
  { value: "schedule", label: "Schedule Changes", placeholder: "Describe the schedule changes you need (days, leads per day, etc.)..." },
  { value: "add_zips", label: "Add Zip Codes", placeholder: "List the zip codes you'd like added, separated by commas..." },
  { value: "remove_zips", label: "Remove Zip Codes", placeholder: "List the zip codes you'd like removed, separated by commas..." },
  { value: "replace_zips", label: "Replace Zip Codes", placeholder: "" },
  { value: "update_recipients", label: "Update Lead Recipients", placeholder: "List the email address(es) you'd like leads sent to (To) and any CC addresses, separated by commas..." },
];

const SettingsSection = ({ settings, isLoading, campaignStatus, showRestart = true }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [requestType, setRequestType] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Replace Zips specific state
  const [newZipsInput, setNewZipsInput] = useState(""); // comma-separated new zips (replaces ALL current)

  // Criteria specific state
  const [criteriaRoofAge, setCriteriaRoofAge] = useState("");

  // Pause / Resume dialog state
  const [showPauseResumeDialog, setShowPauseResumeDialog] = useState(false);
  const [pauseResumeSubmitting, setPauseResumeSubmitting] = useState(false);

  const handleSubmitPauseResume = async () => {
    const action = campaignStatus === "paused" ? "resume" : "pause";
    const message = action === "pause"
      ? "Customer requested to pause their campaign."
      : "Customer requested to resume their campaign.";
    setPauseResumeSubmitting(true);
    try {
      await axios.post(`${API}/customer/change-request`, {
        request_type: "pause_resume",
        message
      });
      setShowPauseResumeDialog(false);
      setShowConfirmDialog(true);
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to submit request. Please try again.";
      toast.error(detail);
    } finally {
      setPauseResumeSubmitting(false);
    }
  };

  // Recordings dialog state
  const recordingsEnabled = /record|yes/i.test(settings?.emails?.recordings || "");
  const [showRecordingsDialog, setShowRecordingsDialog] = useState(false);
  const [recordingsSubmitting, setRecordingsSubmitting] = useState(false);

  const handleSubmitRecordings = async () => {
    const message = recordingsEnabled
      ? "Customer requested to disable call recordings."
      : "Customer requested to enable call recordings.";
    setRecordingsSubmitting(true);
    try {
      await axios.post(`${API}/customer/change-request`, {
        request_type: "recordings",
        message
      });
      setShowRecordingsDialog(false);
      setShowConfirmDialog(true);
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to submit request. Please try again.";
      toast.error(detail);
    } finally {
      setRecordingsSubmitting(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const openRequest = (type) => {
    setRequestType(type);
    setRequestMessage("");
    setNewZipsInput("");
    setCriteriaRoofAge("");
    setShowRequestDialog(true);
  };

  const parseZipList = (str) =>
    str.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => /^\d{5}$/.test(s));

  const handleSubmitRequest = async () => {
    if (!requestType) {
      toast.error("Please select a request type.");
      return;
    }

    let messageToSend = requestMessage.trim();

    if (requestType === "replace_zips") {
      const newZips = parseZipList(newZipsInput);
      if (newZips.length === 0) {
        toast.error("Please enter the new zip codes.");
        return;
      }
      const currentZips = settings.zip_codes || [];
      messageToSend = `Remove ALL current zip codes (${currentZips.length}): ${currentZips.join(", ")}\nAdd: ${newZips.join(", ")}`;
    } else if (requestType === "criteria") {
      if (!criteriaRoofAge && !messageToSend) {
        toast.error("Please select a Roof Age or describe the changes you'd like to make.");
        return;
      }
      const parts = [];
      if (criteriaRoofAge) parts.push(`Roof Age: ${criteriaRoofAge}`);
      if (messageToSend) parts.push(`Additional Notes:\n${messageToSend}`);
      messageToSend = parts.join("\n\n");
    } else if (!messageToSend) {
      toast.error("Please describe the changes you'd like to make.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/customer/change-request`, {
        request_type: requestType,
        message: messageToSend
      });
      setShowRequestDialog(false);
      setRequestMessage("");
      setRequestType("");
      setShowConfirmDialog(true);
    } catch (error) {
      console.error("Failed to submit change request:", error);
      const detail = error.response?.data?.detail || "Failed to send request. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-center justify-center h-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#f4cc1e]"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return null;
  }

  return (
    <>
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg overflow-hidden" data-testid="settings-section">
        <CardHeader className="py-3 px-4 border-b border-[#1a3a6a]">
          <div className="flex items-center gap-2">
            <Settings size={18} className="text-[#f4cc1e]" />
            <CardTitle className="font-['Chivo'] text-base text-white">Campaign Settings</CardTitle>
            {settings.location && (
              <Badge variant="outline" className="ml-2 text-xs border-[#f4cc1e]/30 text-[#f4cc1e]">
                <MapPin size={12} className="mr-1" />
                {settings.location}
              </Badge>
            )}
            {campaignStatus === "active" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPauseResumeDialog(true)}
                className="ml-auto border-[#f4cc1e]/40 text-[#f4cc1e] hover:bg-[#f4cc1e]/10 text-xs h-7"
              >
                <Pause size={12} className="mr-1" /> Request Pause
              </Button>
            )}
            {campaignStatus === "paused" && showRestart && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPauseResumeDialog(true)}
                className="ml-auto border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs h-7"
              >
                <Play size={12} className="mr-1" /> Request Resume
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Zip Codes Section - READ ONLY */}
          <Collapsible open={expandedSection === 'zips'} onOpenChange={() => toggleSection('zips')}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#0d2a5a] transition-colors border-b border-[#1a3a6a]">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-[#f4cc1e]" />
                  <span className="text-sm font-medium text-white">Zip Codes</span>
                  <Badge variant="secondary" className="bg-[#1a3a6a] text-white/70 text-xs">
                    {settings.zip_codes?.length || 0} areas
                  </Badge>
                </div>
                {expandedSection === 'zips' ? (
                  <ChevronDown size={16} className="text-white/50" />
                ) : (
                  <ChevronRight size={16} className="text-white/50" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 py-3 bg-[#0a1628]">
                {settings.zip_codes?.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {settings.zip_codes.map((zip, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="bg-[#091c3c] border-[#1a3a6a] text-white/80 text-xs font-mono"
                      >
                        {zip}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/50 text-sm">No zip codes configured</p>
                )}
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#1a3a6a]">
                  <Button size="sm" variant="outline" onClick={() => openRequest("add_zips")} className="border-[#f4cc1e]/30 text-[#f4cc1e] hover:bg-[#f4cc1e]/10 text-xs h-7">
                    <MapPin size={12} className="mr-1" /> Add Zip Codes
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openRequest("remove_zips")} className="border-[#f4cc1e]/30 text-[#f4cc1e] hover:bg-[#f4cc1e]/10 text-xs h-7">
                    <X size={12} className="mr-1" /> Remove Zip Codes
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openRequest("replace_zips")} className="border-[#f4cc1e]/30 text-[#f4cc1e] hover:bg-[#f4cc1e]/10 text-xs h-7">
                    <Repeat size={12} className="mr-1" /> Replace Zip Codes
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Schedule Section - READ ONLY */}
          <Collapsible open={expandedSection === 'schedule'} onOpenChange={() => toggleSection('schedule')}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#0d2a5a] transition-colors border-b border-[#1a3a6a]">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-[#f4cc1e]" />
                  <span className="text-sm font-medium text-white">Schedule</span>
                  {settings.schedule?.leads_per_day && (
                    <Badge variant="secondary" className="bg-[#1a3a6a] text-white/70 text-xs">
                      {settings.schedule.leads_per_day} leads/day
                    </Badge>
                  )}
                </div>
                {expandedSection === 'schedule' ? (
                  <ChevronDown size={16} className="text-white/50" />
                ) : (
                  <ChevronRight size={16} className="text-white/50" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 py-3 bg-[#0a1628]">
                {settings.schedule ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-white/50 mb-2">Weekly Availability</p>
                      <div className="grid grid-cols-6 gap-1.5">
                        {['sat', 'mon', 'tue', 'wed', 'thu', 'fri'].map((day) => (
                          <div key={day} className="text-center">
                            <p className="text-[10px] text-white/40 uppercase mb-1">{day}</p>
                            <Badge
                              variant={settings.schedule.days?.[day] === 'Full' ? 'default' : 'outline'}
                              className={`w-full justify-center text-[10px] ${
                                settings.schedule.days?.[day] === 'Full'
                                  ? 'bg-green-600 text-white'
                                  : settings.schedule.days?.[day]
                                    ? 'bg-[#091c3c] border-yellow-500/30 text-yellow-400'
                                    : 'bg-[#091c3c] border-[#1a3a6a] text-white/30'
                              }`}
                            >
                              {settings.schedule.days?.[day] || '-'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-[#1a3a6a]">
                      <div>
                        <p className="text-[10px] text-white/40">Appts/Hour</p>
                        <p className="text-sm text-white font-medium">{settings.schedule.appts_per_hour || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40">Reps</p>
                        <p className="text-sm text-white font-medium">{settings.schedule.reps || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40">Same Days</p>
                        <p className="text-sm text-white font-medium">{settings.schedule.same_days || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40">Double Book</p>
                        <p className="text-sm text-white font-medium">{settings.schedule.double_book || '-'}</p>
                      </div>
                    </div>

                    {settings.schedule.notes && (
                      <div className="pt-2 border-t border-[#1a3a6a]">
                        <p className="text-[10px] text-white/40">Notes</p>
                        <p className="text-sm text-white/80">{settings.schedule.notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-white/50 text-sm">No schedule configured</p>
                )}
                <div className="mt-3 pt-3 border-t border-[#1a3a6a]">
                  <Button size="sm" variant="outline" onClick={() => openRequest("schedule")} className="border-[#f4cc1e]/30 text-[#f4cc1e] hover:bg-[#f4cc1e]/10 text-xs h-7">
                    <MessageSquare size={12} className="mr-1" /> Request Change
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Criteria Section - READ ONLY */}
          <Collapsible open={expandedSection === 'criteria'} onOpenChange={() => toggleSection('criteria')}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#0d2a5a] transition-colors">
                <div className="flex items-center gap-2">
                  <ClipboardList size={16} className="text-[#f4cc1e]" />
                  <span className="text-sm font-medium text-white">Criteria</span>
                  {settings.criteria?.roof_age && (
                    <Badge variant="secondary" className="bg-[#1a3a6a] text-white/70 text-xs">
                      Roof {settings.criteria.roof_age}
                    </Badge>
                  )}
                </div>
                {expandedSection === 'criteria' ? (
                  <ChevronDown size={16} className="text-white/50" />
                ) : (
                  <ChevronRight size={16} className="text-white/50" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 py-3 bg-[#0a1628]">
                {settings.criteria ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {[
                        { key: 'denials', label: 'Denials' },
                        { key: 'cash_jobs', label: 'Cash Jobs' },
                        { key: 'financing', label: 'Financing' },
                        { key: 'spanish', label: 'Spanish' },
                        { key: 'roof_age', label: 'Roof Age' },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between bg-[#091c3c] rounded-lg px-3 py-2">
                          <span className="text-xs text-white/60">{label}</span>
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              settings.criteria[key] === 'YES'
                                ? 'border-green-500/30 text-green-400'
                                : settings.criteria[key] === 'NO'
                                  ? 'border-red-500/30 text-red-400'
                                  : 'border-[#1a3a6a] text-white/70'
                            }`}
                          >
                            {settings.criteria[key] || '-'}
                          </Badge>
                        </div>
                      ))}
                    </div>

                    {settings.criteria.roof_types && (
                      <div className="pt-2 border-t border-[#1a3a6a]">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white/50 mb-2">Roof Types Accepted</p>
                            <div className="flex flex-wrap gap-1.5">
                              {Object.entries(settings.criteria.roof_types).map(([type, value]) => (
                                <Badge
                                  key={type}
                                  variant="outline"
                                  className={`text-xs capitalize ${
                                    value === 'YES'
                                      ? 'bg-green-600/10 border-green-500/30 text-green-400'
                                      : 'bg-[#091c3c] border-[#1a3a6a] text-white/30'
                                  }`}
                                >
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 bg-[#091c3c] rounded-lg px-3 py-2 flex-shrink-0">
                            <Mic size={14} className="text-white/60" />
                            <span className="text-xs text-white/70">Recordings:</span>
                            <Badge
                              variant="outline"
                              className={`text-xs ${recordingsEnabled
                                ? "border-green-500/40 text-green-400"
                                : "border-red-500/40 text-red-400"}`}
                            >
                              {recordingsEnabled ? "Yes" : "No"}
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowRecordingsDialog(true)}
                              className="text-[#f4cc1e] hover:bg-[#1a3a6a] h-7 px-2 text-xs"
                            >
                              Request {recordingsEnabled ? "Off" : "On"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="pt-2 border-t border-[#1a3a6a]">
                      <p className="text-xs text-white/50 mb-2">Upgrades</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center justify-between bg-[#091c3c] rounded-lg px-3 py-2">
                          <span className="text-xs text-white/60">Min Hail Size</span>
                          <span className="text-xs text-white/90">{settings.criteria.min_hail_size || "–"}</span>
                        </div>
                        <div className="flex items-center justify-between bg-[#091c3c] rounded-lg px-3 py-2">
                          <span className="text-xs text-white/60">Min Wind Speed</span>
                          <span className="text-xs text-white/90">{settings.criteria.min_wind_speed || "–"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-white/50 text-sm">No criteria configured</p>
                )}
                <div className="mt-3 pt-3 border-t border-[#1a3a6a]">
                  <Button size="sm" variant="outline" onClick={() => openRequest("criteria")} className="border-[#f4cc1e]/30 text-[#f4cc1e] hover:bg-[#f4cc1e]/10 text-xs h-7">
                    <MessageSquare size={12} className="mr-1" /> Request Change
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Lead Recipients Section - READ ONLY */}
          <Collapsible open={expandedSection === 'recipients'} onOpenChange={() => toggleSection('recipients')}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#0d2a5a] transition-colors">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-[#f4cc1e]" />
                  <span className="text-sm font-medium text-white">Lead Recipients</span>
                  {(() => {
                    const total = (settings.emails?.to?.length || 0) + (settings.emails?.cc?.length || 0);
                    return total > 0 ? (
                      <Badge variant="secondary" className="bg-[#1a3a6a] text-white/70 text-xs">
                        {total} {total === 1 ? "address" : "addresses"}
                      </Badge>
                    ) : null;
                  })()}
                </div>
                {expandedSection === 'recipients' ? (
                  <ChevronDown size={16} className="text-white/50" />
                ) : (
                  <ChevronRight size={16} className="text-white/50" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 py-3 bg-[#0a1628] space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/50 mb-1.5 font-medium">To</p>
                  {settings.emails?.to?.length > 0 ? (
                    <p className="text-sm text-white/80 font-mono break-all">
                      {settings.emails.to.join(", ")}
                    </p>
                  ) : (
                    <p className="text-white/50 text-sm italic">No recipients configured</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/50 mb-1.5 font-medium">CC</p>
                  {settings.emails?.cc?.length > 0 ? (
                    <p className="text-sm text-white/80 font-mono break-all">
                      {settings.emails.cc.join(", ")}
                    </p>
                  ) : (
                    <p className="text-white/50 text-sm italic">None</p>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-[#1a3a6a]">
                  <Button size="sm" variant="outline" onClick={() => openRequest("update_recipients")} className="border-[#f4cc1e]/30 text-[#f4cc1e] hover:bg-[#f4cc1e]/10 text-xs h-7">
                    <MessageSquare size={12} className="mr-1" /> Request Change
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Request Changes Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={(open) => {
        setShowRequestDialog(open);
        if (!open) { setRequestType(""); setRequestMessage(""); }
      }}>
        <DialogContent className={`bg-[#091c3c] border-[#1a3a6a] text-white ${requestType === "replace_zips" ? "max-w-lg" : "max-w-md"}`}>
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white flex items-center gap-2">
              <MessageSquare size={20} className="text-[#f4cc1e]" />
              {REQUEST_TYPE_OPTIONS.find(o => o.value === requestType)?.label || "Request Change"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {requestType === "replace_zips" ? (
              <>
                <p className="text-sm text-white/60">
                  This will replace ALL of your current zip codes ({settings.zip_codes?.length || 0}) with the list you enter below.
                </p>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/50 mb-2 font-medium">New Zip Codes</p>
                  <Textarea
                    value={newZipsInput}
                    onChange={(e) => setNewZipsInput(e.target.value)}
                    placeholder="Enter new zip codes, separated by commas (e.g. 55125, 55129, 55343)"
                    className="bg-[#0a1628] border-[#1a3a6a] text-white text-sm min-h-[120px] placeholder:text-white/30 font-mono"
                    maxLength={2000}
                  />
                  {(() => {
                    const parsedCount = parseZipList(newZipsInput).length;
                    return (
                      <p className={`text-xs mt-1.5 ${parsedCount > 0 ? "text-green-400" : "text-white/40"}`}>
                        {parsedCount} new zip code{parsedCount === 1 ? "" : "s"} entered
                      </p>
                    );
                  })()}
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-white/60">
                  Describe what you need changed. Our team will review and update your settings.
                </p>
                {requestType === "criteria" && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/50 mb-2 font-medium">Roof Age</p>
                    <Select value={criteriaRoofAge} onValueChange={setCriteriaRoofAge}>
                      <SelectTrigger className="bg-[#0a1628] border-[#1a3a6a] text-white">
                        <SelectValue placeholder="Select roof age requirement" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-[#1a3a6a]">
                        <SelectItem value="Any Age" className="text-white">Any Age</SelectItem>
                        <SelectItem value="5+ years" className="text-white">5+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder={REQUEST_TYPE_OPTIONS.find(o => o.value === requestType)?.placeholder || "Describe the changes you need..."}
                  className="bg-[#0a1628] border-[#1a3a6a] text-white text-sm min-h-[150px] placeholder:text-white/30"
                  maxLength={5000}
                />
                <div className="text-xs text-white/30">{requestMessage.length}/5000</div>
              </>
            )}
            <div className="flex items-center justify-end">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowRequestDialog(false)}
                  className="border-[#1a3a6a] text-white hover:bg-[#1a3a6a]"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmitRequest}
                  disabled={submitting || (
                    requestType === "replace_zips"
                      ? parseZipList(newZipsInput).length === 0
                      : requestType === "criteria"
                        ? (!criteriaRoofAge && !requestMessage.trim())
                        : !requestMessage.trim()
                  )}
                  className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
                >
                  {submitting ? (
                    <RefreshCw size={14} className="mr-1 animate-spin" />
                  ) : (
                    <Send size={14} className="mr-1" />
                  )}
                  Send Request
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white flex items-center gap-2">
              <CheckCircle size={20} className="text-green-400" />
              Request Received
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-white/80">
              We have received your request. All changes can take 24 hours to be implemented.
            </p>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => setShowConfirmDialog(false)}
                className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
              >
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pause / Resume Confirmation Dialog */}
      <Dialog open={showPauseResumeDialog} onOpenChange={setShowPauseResumeDialog}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white flex items-center gap-2">
              {campaignStatus === "paused" ? (
                <Play size={20} className="text-green-400" />
              ) : (
                <Pause size={20} className="text-[#f4cc1e]" />
              )}
              {campaignStatus === "paused" ? "Resume Your Campaign?" : "Pause Your Campaign?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-white/80">
              {campaignStatus === "paused"
                ? "We'll forward your request to restart your calling campaign. Changes typically take up to 24 hours to process."
                : "We'll forward your request to pause all outbound calling. Changes typically take up to 24 hours to process."}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPauseResumeDialog(false)}
                disabled={pauseResumeSubmitting}
                className="border-[#1a3a6a] text-white hover:bg-[#1a3a6a]"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmitPauseResume}
                disabled={pauseResumeSubmitting}
                className={campaignStatus === "paused"
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"}
              >
                {pauseResumeSubmitting ? (
                  <RefreshCw size={14} className="mr-1 animate-spin" />
                ) : campaignStatus === "paused" ? (
                  <Play size={14} className="mr-1" />
                ) : (
                  <Pause size={14} className="mr-1" />
                )}
                {pauseResumeSubmitting
                  ? "Sending..."
                  : campaignStatus === "paused" ? "Yes, Resume" : "Yes, Pause"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recordings Confirmation Dialog */}
      <Dialog open={showRecordingsDialog} onOpenChange={setShowRecordingsDialog}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white flex items-center gap-2">
              <Mic size={20} className={recordingsEnabled ? "text-red-400" : "text-green-400"} />
              {recordingsEnabled ? "Turn Recordings Off?" : "Turn Recordings On?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-white/80">
              {recordingsEnabled
                ? "We'll forward your request to disable call recordings. Changes typically take up to 24 hours to process."
                : "We'll forward your request to enable call recordings on your campaign. Changes typically take up to 24 hours to process."}
            </p>
            {!recordingsEnabled && (
              <p className="text-sm text-[#f4cc1e] bg-[#f4cc1e]/10 border border-[#f4cc1e]/30 rounded-md px-3 py-2">
                <strong>Heads up:</strong> Recordings are billed at <strong>$100 per 10-pack</strong>.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRecordingsDialog(false)}
                disabled={recordingsSubmitting}
                className="border-[#1a3a6a] text-white hover:bg-[#1a3a6a]"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmitRecordings}
                disabled={recordingsSubmitting}
                className={recordingsEnabled
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-green-600 text-white hover:bg-green-700"}
              >
                {recordingsSubmitting ? (
                  <RefreshCw size={14} className="mr-1 animate-spin" />
                ) : (
                  <Mic size={14} className="mr-1" />
                )}
                {recordingsSubmitting
                  ? "Sending..."
                  : recordingsEnabled ? "Yes, Turn Off" : "Yes, Turn On"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
const CustomerDashboard = () => {
  const { user, switchCompany } = useAuth();
  const [switchingCompany, setSwitchingCompany] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("daily");
  
  // Customer settings from Google Sheet (Zips, Schedule, Criteria)
  const [customerSettings, setCustomerSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  const [orderMoreSubmitting, setOrderMoreSubmitting] = useState(false);
  const [reactivateSubmitting, setReactivateSubmitting] = useState(false);

  // Customer order data from Overall Pay Sheet
  const [customerOrder, setCustomerOrder] = useState({
    ordered: 0,
    appointments_set: 0,
    credits: 0,
    remaining: 0,
    health: "",
    status: "",
    paused_by_request_at: null,
    account_active: true
  });
  const [csLeadCount, setCsLeadCount] = useState(0);
  
  // Call history from customer's Google Sheet tab
  const [callHistory, setCallHistory] = useState([]);
  const [callHistoryMeta, setCallHistoryMeta] = useState({
    tabFound: false,
    tabName: "",
    qualifiedCount: 0,
    creditsCount: 0
  });

  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    axios.get(`${API}/quickbooks/invoices`)
      .then(({ data }) => setInvoices(data || []))
      .catch(() => setInvoices([]));
  }, []);
  
  // Result options and saved results
  const [resultOptions, setResultOptions] = useState([]);
  const [callResults, setCallResults] = useState({});
  
  // Search, Filter, and Sort states
  const [searchQuery, setSearchQuery] = useState("");
  const [zipSearchQuery, setZipSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, credits
  const [filterZips, setFilterZips] = useState([]); // array of selected zip codes
  const [filterResults, setFilterResults] = useState([]); // New state for result filter
  const [resultSearchQuery, setResultSearchQuery] = useState(""); // New state for result search
  const [sortConfig, setSortConfig] = useState({ key: "date_of_call", direction: "desc" });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchCustomerOrder(),  // Get KPI values from Overall Pay Sheet
      fetchCustomerSettings(),
      fetchCallHistory(),  // Get call history and charts from customer's tab
      fetchResultOptions(),
      fetchCallResults(),
      fetchCsLeads()
    ]);
    setLoading(false);
  };

  const fetchCsLeads = async () => {
    try {
      const response = await axios.get(`${API}/customer/customer-service-leads`);
      setCsLeadCount(response.data?.total_count || 0);
    } catch (error) {
      console.error("Failed to fetch customer service leads:", error);
    }
  };

  const fetchCustomerOrder = async () => {
    try {
      const response = await axios.get(`${API}/customer/order-summary`);
      if (response.data) {
        setCustomerOrder({
          ordered: response.data.appointments_sold || 0,
          appointments_set: response.data.appointments_set || 0,
          credits: response.data.credits || 0,
          remaining: response.data.appointments_remaining || 0,
          health: response.data.health || "",
          status: response.data.status || "",
          paused_by_request_at: response.data.paused_by_request_at || null,
          account_active: response.data.account_active !== false
        });
      }
    } catch (error) {
      console.error("Failed to fetch customer order:", error);
    }
  };

  const handleRequestReactivation = async () => {
    setReactivateSubmitting(true);
    try {
      await axios.post(`${API}/customer/request-reactivation`);
      toast.success("Your reactivation request has been sent! We'll be in touch shortly.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reactivation request. Please try again.");
    } finally {
      setReactivateSubmitting(false);
    }
  };

  const fetchCustomerSettings = async () => {
    try {
      const response = await axios.get(`${API}/customer/settings`);
      setCustomerSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch customer settings:", error);
      // Set default empty settings so the section still shows
      setCustomerSettings({
        zip_codes: [],
        schedule: {
          leads_per_day: 0,
          days: { sat: "", mon: "", tue: "", wed: "", thu: "", fri: "" },
          appts_per_hour: "",
          reps: "",
          same_days: "",
          double_book: "",
          notes: ""
        },
        criteria: {
          roof_age: "",
          denials: "",
          cash_jobs: "",
          financing: "",
          spanish: "",
          roof_types: { shingles: "", tile: "", metal: "", flat: "", shake: "", slate: "" },
          notes: ""
        },
        location: ""
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchCallHistory = async () => {
    try {
      const response = await axios.get(`${API}/customer/call-history`);
      if (response.data) {
        setCallHistory(response.data.call_history || []);
        setCallHistoryMeta({
          tabFound: response.data.tab_found || false,
          tabName: response.data.tab_name || "",
          qualifiedCount: response.data.qualified_count || 0,
          creditsCount: response.data.credits_count || 0
        });
        // Note: KPI cards are now set by fetchCustomerOrder from Overall Pay Sheet
      }
    } catch (error) {
      console.error("Failed to fetch call history:", error);
    }
  };

  const fetchResultOptions = async () => {
    try {
      const response = await axios.get(`${API}/customer/result-options`);
      setResultOptions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch result options:", error);
    }
  };

  const fetchCallResults = async () => {
    try {
      const response = await axios.get(`${API}/customer/call-results`);
      setCallResults(response.data || {});
    } catch (error) {
      console.error("Failed to fetch call results:", error);
    }
  };

  const handleResultChange = async (callId, result) => {
    try {
      await axios.put(`${API}/customer/call-results`, {
        call_id: callId,
        result: result
      });
      setCallResults(prev => ({ ...prev, [callId]: result }));
      toast.success("Result saved");
    } catch (error) {
      console.error("Failed to update result:", error);
      toast.error("Failed to save result");
    }
  };

  // Generate a unique call ID from the call data
  const getCallId = (log) => {
    return `${log.date_of_call || ''}_${log.ho_manager_name || ''}_${log.phone_number || ''}`.replace(/\s+/g, '_');
  };

  // Chart data from call history
  const chartData = useMemo(() => {
    const grouped = {};
    
    callHistory.forEach(log => {
      // Parse date - handle various formats
      let dateKey = log.date_of_call || "";
      if (viewMode === "monthly" && dateKey) {
        // Try to extract month/year
        const parts = dateKey.split(" ");
        if (parts.length >= 2) {
          dateKey = `${parts[0]} ${parts[parts.length - 1]}`; // Month Year
        }
      }
      
      if (!dateKey) return;
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = { date: dateKey, total: 0, qualified: 0, credits: 0 };
      }
      grouped[dateKey].total++;
      if (log.qualified_payable === "Y") grouped[dateKey].qualified++;
      if (log.qualified_payable === "N") grouped[dateKey].credits++;
    });

    return Object.values(grouped)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [callHistory, viewMode]);

  // Pie chart data
  const statusData = [
    { name: 'Qualified', value: callHistoryMeta.qualifiedCount, color: '#22C55E' },
    { name: 'Credits', value: callHistoryMeta.creditsCount, color: '#EF4444' },
  ].filter(d => d.value > 0);

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={12} className="ml-1 text-white/30" />;
    }
    return sortConfig.direction === "asc" 
      ? <ArrowUp size={12} className="ml-1 text-[#f4cc1e]" />
      : <ArrowDown size={12} className="ml-1 text-[#f4cc1e]" />;
  };

  // Extract unique zip codes from addresses
  const uniqueZipCodes = useMemo(() => {
    const zips = new Set();
    callHistory.forEach(log => {
      if (log.property_address) {
        // Extract zip code (last 5 digits in address)
        const match = log.property_address.match(/\b\d{5}\b/);
        if (match) {
          zips.add(match[0]);
        }
      }
    });
    return Array.from(zips).sort();
  }, [callHistory]);

  // Calculate counts for filter chips
  const statusCounts = useMemo(() => {
    const credits = callHistory.filter(log => {
      // Credits from Google Sheets (Payable = N)
      if (log.is_credit) return true;
      
      // Credits from dispositions that generate credits
      const callId = getCallId(log);
      const currentResult = callResults[callId];
      if (currentResult) {
        const option = resultOptions.find(opt => opt.name === currentResult);
        if (option?.generates_credit) return true;
      }
      
      return false;
    }).length;
    
    return { all: callHistory.length, credits };
  }, [callHistory, callResults, resultOptions]);

  // Filter and sort
  const filteredAndSortedLogs = useMemo(() => {
    let filtered = callHistory;
    
    // Apply status filter
    if (filterStatus === 'credits') {
      filtered = filtered.filter(log => {
        // Credits from Google Sheets (Payable = N)
        if (log.is_credit) return true;
        
        // Credits from dispositions that generate credits
        const callId = getCallId(log);
        const currentResult = callResults[callId];
        if (currentResult) {
          const option = resultOptions.find(opt => opt.name === currentResult);
          if (option?.generates_credit) return true;
        }
        
        return false;
      });
    }
    
    // Apply zip code filter
    if (filterZips.length > 0) {
      filtered = filtered.filter(log => {
        if (!log.property_address) return false;
        const match = log.property_address.match(/\b\d{5}\b/);
        return match && filterZips.includes(match[0]);
      });
    }
    
    // Apply result filter
    if (filterResults.length > 0) {
      filtered = filtered.filter(log => {
        const callId = getCallId(log);
        const currentResult = callResults[callId];
        return currentResult && filterResults.includes(currentResult);
      });
    }
    
    // Apply text search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => 
        log.date_of_call?.toLowerCase().includes(query) ||
        log.ho_manager_name?.toLowerCase().includes(query) ||
        log.phone_number?.toLowerCase().includes(query) ||
        log.property_address?.toLowerCase().includes(query) ||
        log.additional_notes?.toLowerCase().includes(query)
      );
    }
    
    return filtered.sort((a, b) => {
      const aVal = a[sortConfig.key] || "";
      const bVal = b[sortConfig.key] || "";

      // Use Date parsing for date columns so they sort chronologically
      if (sortConfig.key === "date_of_call" || sortConfig.key === "inspection_datetime") {
        const aDate = new Date(aVal);
        const bDate = new Date(bVal);
        if (!isNaN(aDate) && !isNaN(bDate)) {
          return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
        }
      }

      if (sortConfig.direction === "asc") {
        return aVal.toString().localeCompare(bVal.toString());
      }
      return bVal.toString().localeCompare(aVal.toString());
    });
  }, [callHistory, searchQuery, sortConfig, filterStatus, filterZips, filterResults, callResults, resultOptions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  // Credit % = Credits / Set. Hide "Order More" when it's above 40%.
  const creditPct = customerOrder.appointments_set > 0
    ? (customerOrder.credits / customerOrder.appointments_set) * 100
    : 0;
  const showOrderMore = creditPct <= 40;

  return (
    <div className="space-y-4 bg-[#f4cc1e] p-4 sm:p-6 rounded-xl min-h-screen" data-testid="customer-dashboard">
      {/* Welcome Header */}
      <div className="bg-[#091c3c] rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-white font-['Chivo']">
              {user?.company_name || user?.name}
            </h1>
            <p className="text-white/60 mt-1">
              Your call tracking dashboard
              {callHistoryMeta.tabFound && (
                <span className="ml-2 text-[#f4cc1e]">• Data from: {callHistoryMeta.tabName}</span>
              )}
            </p>
          </div>
          {user?.company_names && user.company_names.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/50 uppercase tracking-wider hidden sm:inline">Company:</span>
              <Select
                value={user.company_name}
                disabled={switchingCompany}
                onValueChange={async (value) => {
                  if (value === user.company_name) return;
                  setSwitchingCompany(true);
                  try {
                    await switchCompany(value);
                    await fetchAllData();
                    toast.success(`Switched to ${value}`);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to switch company");
                  } finally {
                    setSwitchingCompany(false);
                  }
                }}
              >
                <SelectTrigger className="w-56 bg-[#0d2a5a] border-[#1a3a6a] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                  {user.company_names.map((name) => (
                    <SelectItem key={name} value={name} className="text-white">
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <button
            onClick={fetchAllData}
            className="p-2 rounded-lg bg-[#0d2a5a] hover:bg-[#1a3a6a] transition-colors"
            title="Refresh data"
          >
            <RefreshCw size={18} className="text-[#f4cc1e]" />
          </button>
        </div>
      </div>

      {/* Expandable Settings Section */}
      <SettingsSection
        settings={customerSettings}
        isLoading={settingsLoading}
        campaignStatus={customerOrder.status}
        showRestart={showOrderMore}
      />

      {/* Leads Remaining, Customer Service Leads (if any), Account Health */}
      <div className={`grid grid-cols-1 ${csLeadCount > 0 ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-3 sm:gap-4`}>
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <p className="text-xs sm:text-sm uppercase tracking-wider font-medium mb-2 text-white/70">
                  Leads Remaining
                </p>
                <p className="text-5xl sm:text-7xl font-bold font-['Chivo'] text-white">
                  {customerOrder.remaining}
                </p>
              </div>
              {!showOrderMore ? null : customerOrder.account_active === false ? (
                <Button
                  size="lg"
                  className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-semibold shadow-lg hover:shadow-xl transition-all"
                  disabled={reactivateSubmitting}
                  onClick={handleRequestReactivation}
                >
                  {reactivateSubmitting ? 'Sending...' : '🔄 Reactivate Account'}
                </Button>
              ) : (
                <Button
                  size="lg"
                  className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-semibold shadow-lg hover:shadow-xl transition-all"
                  disabled={orderMoreSubmitting}
                  onClick={async () => {
                    setOrderMoreSubmitting(true);
                    try {
                      await axios.post(`${API}/customer/request-order-more`);
                      toast.success("Your order request has been sent! We'll process it shortly.");
                    } catch (error) {
                      const detail = error.response?.data?.detail || "Failed to send order request. Please try again.";
                      toast.error(detail);
                    } finally {
                      setOrderMoreSubmitting(false);
                    }
                  }}
                >
                  {orderMoreSubmitting ? 'Sending...' : '📞 Buy More Leads'}
                </Button>
              )}
            </div>
            {customerOrder.account_active === false && (
              <p className="text-sm text-amber-400/90 mt-3 text-center font-medium">
                Your account is inactive. Reactivate to start receiving leads again — these are your last recorded totals.
              </p>
            )}
          </CardContent>
        </Card>
        {csLeadCount > 0 && (
          <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-xs sm:text-sm uppercase tracking-wider font-medium mb-2 text-white/70">
                  Customer Service Leads
                </p>
                <p className="text-5xl sm:text-7xl font-bold font-['Chivo'] text-[#f4cc1e]">
                  {csLeadCount}
                </p>
                <p className="text-xs text-white/50 mt-2">Bonus leads provided</p>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-xs sm:text-sm uppercase tracking-wider font-medium mb-2 text-white/70">
                Account Health
              </p>
              <p className={`text-5xl sm:text-7xl font-bold font-['Chivo'] ${
                /good/i.test(customerOrder.health) ? "text-green-400" :
                /caution/i.test(customerOrder.health) ? "text-yellow-400" :
                /bad/i.test(customerOrder.health) ? "text-red-400" :
                /paused/i.test(customerOrder.health) ? "text-red-500" :
                "text-white/50"
              }`}>
                {customerOrder.health || "—"}
              </p>
              {/caution/i.test(customerOrder.health) && (
                <p className="text-sm text-yellow-400/80 mt-3">Please provide additional zip codes before your campaign is paused.</p>
              )}
              {/bad/i.test(customerOrder.health) && (
                <p className="text-sm text-red-400/80 mt-3 font-medium">Urgent, your campaign will be paused soon. Please provide more zip codes ASAP.</p>
              )}
              {/paused/i.test(customerOrder.health) && (
                customerOrder.paused_by_request_at ? (
                  <p className="text-sm text-red-500/80 mt-3 font-medium">
                    You requested your campaign be paused on {new Date(customerOrder.paused_by_request_at).toLocaleDateString()}. Click the Request Resume button to get more leads!
                  </p>
                ) : (
                  <p className="text-sm text-red-500/80 mt-3 font-medium">We have run out of zip codes. Please submit more so we can resume your campaign.</p>
                )
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">{/* Note: changed to 3 columns since Remaining is now above */}
        <KPICard
          title="Ordered"
          value={customerOrder.ordered}
          subtitle="Total purchased"
          icon={ShoppingCart}
          color="secondary"
        />
        <KPICard
          title="Appointments Set"
          value={customerOrder.appointments_set}
          subtitle="Qualified leads"
          icon={CheckCircle}
          color="success"
        />
        <KPICard
          title="Credits"
          value={customerOrder.credits}
          subtitle="Not qualified"
          icon={AlertCircle}
          color="danger"
        />
      </div>
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Trend Chart */}
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl lg:col-span-2 shadow-lg">
          <CardHeader className="border-b border-[#1a3a6a] pb-4 px-4 sm:px-6">
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Chivo'] text-base sm:text-lg text-white">Appointment Trends</CardTitle>
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-28 sm:w-32 h-8 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white" data-testid="view-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {chartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-white/50">
                No appointment data to display
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData.slice(-14)} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a3a6a" />
                    <XAxis dataKey="date" stroke="#6b7280" fontSize={10} tick={{ fill: '#9ca3af' }} interval={0} angle={-45} textAnchor="end" height={60} />
                    <YAxis stroke="#6b7280" fontSize={11} tick={{ fill: '#9ca3af' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#091c3c',
                        border: '1px solid #1a3a6a',
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                      labelStyle={{ color: '#f4cc1e' }}
                      itemStyle={{ color: '#ffffff' }}
                    />
                    <Bar dataKey="qualified" name="Qualified" fill="#22C55E" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="credits" name="Credits" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      name="Total" 
                      stroke="#f4cc1e" 
                      strokeWidth={3}
                      dot={{ fill: '#f4cc1e', strokeWidth: 0, r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Breakdown Pie */}
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
          <CardHeader className="border-b border-[#1a3a6a] pb-4 px-4 sm:px-6">
            <CardTitle className="font-['Chivo'] text-base sm:text-lg text-white">Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {statusData.length === 0 ? (
              <div className="h-52 flex items-center justify-center text-white/50">
                No status data
              </div>
            ) : (
              <>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#091c3c',
                          border: '1px solid #1a3a6a',
                          borderRadius: '8px',
                          color: '#ffffff'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap justify-center gap-4 mt-2">
                  {statusData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-white/70">{item.name} ({item.value})</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Billing */}
      {invoices.length > 0 && (
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
          <CardHeader className="border-b border-[#1a3a6a] pb-4 px-4 sm:px-6">
            <CardTitle className="font-['Chivo'] text-base sm:text-lg text-white">Billing</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-2">
              {invoices.map((iv) => (
                <div key={iv.qbo_invoice_id} className="flex items-center justify-between text-sm">
                  <span className="text-white/80">
                    Invoice #{iv.doc_number || iv.qbo_invoice_id}
                    {iv.due_date ? ` · due ${iv.due_date}` : ""}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-white/70">${iv.balance?.toFixed(2)} / ${iv.total_amount?.toFixed(2)}</span>
                    <span className={
                      iv.status === "paid" ? "text-green-400 font-medium"
                      : iv.status === "partial" ? "text-amber-400 font-medium"
                      : "text-white/60 font-medium"
                    }>
                      {iv.status === "paid" ? "Paid" : iv.status === "partial" ? "Partially paid" : "Open"}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Call History Table */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="border-b border-[#1a3a6a] pb-4 px-4 sm:px-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Chivo'] text-base sm:text-lg text-white flex items-center gap-2">
                <FileText size={20} className="text-[#f4cc1e]" />
                Call History
              </CardTitle>
              <span className="text-sm text-white/50">{filteredAndSortedLogs.length} records</span>
            </div>
            
            {/* Filter Bar */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-white/60 mr-1">Show:</span>
              <Button
                size="sm"
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
                className={filterStatus === 'all' 
                  ? 'bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 h-8 text-xs' 
                  : 'border-[#1a3a6a] text-white/70 hover:bg-[#1a3a6a] h-8 text-xs'}
              >
                All Calls
                <Badge variant="secondary" className="ml-1.5 bg-white/20 text-white px-1.5 py-0">
                  {statusCounts.all}
                </Badge>
              </Button>
              <Button
                size="sm"
                variant={filterStatus === 'credits' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('credits')}
                className={filterStatus === 'credits' 
                  ? 'bg-red-600 text-white hover:bg-red-700 h-8 text-xs' 
                  : 'border-[#1a3a6a] text-white/70 hover:bg-[#1a3a6a] h-8 text-xs'}
              >
                <CreditCard size={14} className="mr-1" />
                Credits
                <Badge variant="secondary" className="ml-1.5 bg-white/20 text-white px-1.5 py-0">
                  {statusCounts.credits}
                </Badge>
              </Button>
              
              <div className="h-6 w-px bg-white/20 mx-1"></div>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#1a3a6a] text-white/70 hover:bg-[#1a3a6a] h-8 text-xs justify-start min-w-[140px]"
                  >
                    <MapPin size={14} className="mr-1.5" />
                    {filterZips.length === 0 
                      ? 'All Zip Codes'
                      : filterZips.length === 1
                      ? filterZips[0]
                      : `${filterZips.length} zips`}
                    <ChevronDown size={14} className="ml-auto" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[240px] p-0 bg-[#0d2a5a] border-[#1a3a6a]" align="start">
                  <div className="p-3 border-b border-[#1a3a6a]">
                    <Input
                      placeholder="Search zip codes..."
                      value={zipSearchQuery}
                      onChange={(e) => setZipSearchQuery(e.target.value)}
                      className="h-8 bg-[#091c3c] border-[#1a3a6a] text-white text-sm"
                    />
                  </div>
                  <div className="p-2 border-b border-[#1a3a6a] bg-[#091c3c]/50">
                    <div 
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#1a3a6a] rounded cursor-pointer"
                      onClick={() => {
                        if (filterZips.length === uniqueZipCodes.length) {
                          setFilterZips([]);
                        } else {
                          setFilterZips([...uniqueZipCodes]);
                        }
                      }}
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                        filterZips.length === uniqueZipCodes.length 
                          ? 'bg-[#f4cc1e] border-[#f4cc1e]' 
                          : filterZips.length > 0
                          ? 'bg-[#f4cc1e]/50 border-[#f4cc1e]'
                          : 'border-white/30'
                      }`}>
                        {filterZips.length === uniqueZipCodes.length && (
                          <CheckCircle size={12} className="text-[#091c3c]" />
                        )}
                        {filterZips.length > 0 && filterZips.length < uniqueZipCodes.length && (
                          <div className="w-2 h-0.5 bg-[#091c3c] rounded"></div>
                        )}
                      </div>
                      <span className="text-white font-medium text-sm">
                        {filterZips.length === uniqueZipCodes.length ? 'Deselect All' : 'Select All'}
                      </span>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-auto p-2">
                    {uniqueZipCodes
                      .filter(zip => !zipSearchQuery || zip.includes(zipSearchQuery))
                      .map((zip) => {
                        const count = callHistory.filter(log => log.property_address?.includes(zip)).length;
                        const isSelected = filterZips.includes(zip);
                        return (
                          <div
                            key={zip}
                            className="flex items-center justify-between px-2 py-2 hover:bg-[#1a3a6a] rounded cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterZips(prev =>
                                isSelected
                                  ? prev.filter(z => z !== zip)
                                  : [...prev, zip]
                              );
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-[#f4cc1e] border-[#f4cc1e]' : 'border-white/30'
                              }`}>
                                {isSelected && <CheckCircle size={12} className="text-[#091c3c]" />}
                              </div>
                              <span className="text-white text-sm">{zip}</span>
                            </div>
                            <Badge variant="secondary" className="bg-white/10 text-white/70 text-xs ml-2">
                              {count}
                            </Badge>
                          </div>
                        );
                      })}
                    {zipSearchQuery && uniqueZipCodes.filter(zip => zip.includes(zipSearchQuery)).length === 0 && (
                      <div className="text-white/50 text-sm py-6 text-center">
                        No zip codes found
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#1a3a6a] text-white/70 hover:bg-[#1a3a6a] h-8 text-xs justify-start min-w-[140px]"
                  >
                    <Settings size={14} className="mr-1.5" />
                    {filterResults.length === 0 
                      ? 'All Results'
                      : filterResults.length === 1
                      ? filterResults[0]
                      : `${filterResults.length} results`}
                    <ChevronDown size={14} className="ml-auto" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[240px] p-0 bg-[#0d2a5a] border-[#1a3a6a]" align="start">
                  <div className="p-3 border-b border-[#1a3a6a]">
                    <Input
                      placeholder="Search results..."
                      value={resultSearchQuery}
                      onChange={(e) => setResultSearchQuery(e.target.value)}
                      className="h-8 bg-[#091c3c] border-[#1a3a6a] text-white text-sm"
                    />
                  </div>
                  <div className="p-2 border-b border-[#1a3a6a] bg-[#091c3c]/50">
                    <div 
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#1a3a6a] rounded cursor-pointer"
                      onClick={() => {
                        if (filterResults.length === resultOptions.length) {
                          setFilterResults([]);
                        } else {
                          setFilterResults(resultOptions.map(opt => opt.name));
                        }
                      }}
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                        filterResults.length === resultOptions.length 
                          ? 'bg-[#f4cc1e] border-[#f4cc1e]' 
                          : filterResults.length > 0
                          ? 'bg-[#f4cc1e]/50 border-[#f4cc1e]'
                          : 'border-white/30'
                      }`}>
                        {filterResults.length === resultOptions.length && (
                          <CheckCircle size={12} className="text-[#091c3c]" />
                        )}
                        {filterResults.length > 0 && filterResults.length < resultOptions.length && (
                          <div className="w-2 h-0.5 bg-[#091c3c] rounded"></div>
                        )}
                      </div>
                      <span className="text-white font-medium text-sm">
                        {filterResults.length === resultOptions.length ? 'Deselect All' : 'Select All'}
                      </span>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-auto p-2">
                    {resultOptions
                      .filter(opt => !resultSearchQuery || opt.name.toLowerCase().includes(resultSearchQuery.toLowerCase()))
                      .map((option) => {
                        const count = callHistory.filter(log => {
                          const callId = getCallId(log);
                          return callResults[callId] === option.name;
                        }).length;
                        const isSelected = filterResults.includes(option.name);
                        return (
                          <div
                            key={option.id}
                            className="flex items-center justify-between px-2 py-2 hover:bg-[#1a3a6a] rounded cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterResults(prev =>
                                isSelected
                                  ? prev.filter(r => r !== option.name)
                                  : [...prev, option.name]
                              );
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-[#f4cc1e] border-[#f4cc1e]' : 'border-white/30'
                              }`}>
                                {isSelected && <CheckCircle size={12} className="text-[#091c3c]" />}
                              </div>
                              <span className="text-white text-sm">{option.name}</span>
                              {option.generates_credit && (
                                <CreditCard size={12} className="text-red-400" />
                              )}
                            </div>
                            <Badge variant="secondary" className="bg-white/10 text-white/70 text-xs ml-2">
                              {count}
                            </Badge>
                          </div>
                        );
                      })}
                    {resultSearchQuery && resultOptions.filter(opt => opt.name.toLowerCase().includes(resultSearchQuery.toLowerCase())).length === 0 && (
                      <div className="text-white/50 text-sm py-6 text-center">
                        No results found
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              {(filterStatus !== 'all' || filterZips.length > 0 || filterResults.length > 0) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setFilterStatus('all');
                    setFilterZips([]);
                    setFilterResults([]);
                  }}
                  className="text-white/50 hover:text-white hover:bg-[#1a3a6a] h-8 text-xs"
                >
                  <X size={14} className="mr-1" />
                  Clear
                </Button>
              )}
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                placeholder="Search by name, phone, address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!callHistoryMeta.tabFound ? (
            <div className="p-8 text-center text-white/50">
              <Phone size={48} className="mx-auto mb-4 opacity-50" />
              <p>No matching Google Sheet tab found</p>
              <p className="text-sm mt-1">Contact support to set up your call history</p>
            </div>
          ) : filteredAndSortedLogs.length === 0 ? (
            <div className="p-8 text-center text-white/50">
              <Phone size={48} className="mx-auto mb-4 opacity-50" />
              <p>{searchQuery ? "No matching records found" : "No calls recorded yet"}</p>
              <p className="text-sm mt-1">{searchQuery ? "Try a different search term" : "Your call history will appear here"}</p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#1a3a6a]">
                    {[
                      { key: 'date_of_call', label: 'Date of Call' },
                      { key: 'inspection_datetime', label: 'Inspection Date/Time' },
                      { key: 'ho_manager_name', label: 'HO/Manager Name' },
                      { key: 'phone_number', label: 'Phone Number' },
                      { key: 'property_address', label: 'Property Address' },
                    ].map(({ key, label }) => (
                      <TableHead 
                        key={key}
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold whitespace-nowrap cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleSort(key)}
                      >
                        <div className="flex items-center">
                          {label} {getSortIcon(key)}
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold whitespace-nowrap">
                      <span
                        className="inline-flex items-center gap-1 cursor-help"
                        title={'"No" means the check-in didn\'t happen, was more than 500 ft from the property, or wasn\'t done within 3 hours of the scheduled appointment time.'}
                      >
                        Valid Check-In
                        <Info size={12} className="text-white/50" />
                      </span>
                    </TableHead>
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold whitespace-nowrap">
                      Results
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedLogs.map((log) => {
                    const callId = getCallId(log);
                    const currentResult = callResults[callId] || "";
                    
                    // Check if this is a credit from either source
                    let isCredit = log.is_credit; // From Google Sheets
                    if (!isCredit && currentResult) {
                      // Check if the selected disposition generates a credit
                      const selectedOption = resultOptions.find(opt => opt.name === currentResult);
                      if (selectedOption?.generates_credit) {
                        isCredit = true;
                      }
                    }
                    
                    return (
                    <TableRow 
                      key={log.id} 
                      className={`border-b border-[#1a3a6a] ${isCredit ? "bg-red-900/20 hover:bg-red-900/30" : "hover:bg-[#0d2a5a]"}`}
                    >
                      <TableCell className="font-mono text-sm text-white whitespace-nowrap">
                        {log.date_of_call || "-"}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-white whitespace-nowrap">
                        {log.inspection_datetime || "-"}
                      </TableCell>
                      <TableCell className="text-white whitespace-nowrap">
                        {log.ho_manager_name || "-"}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-white whitespace-nowrap">
                        {log.phone_number || "-"}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <span className="text-sm text-white">{log.property_address || "-"}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {log.valid_checkin ? (
                          <span className="text-green-400 font-semibold">Yes</span>
                        ) : (
                          <span className="flex flex-col leading-tight">
                            <span className="text-red-400 font-semibold">No</span>
                            {log.checkin_reason && (
                              <span className="text-[10px] text-white/70">{log.checkin_reason}</span>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[150px]">
                        {(() => {
                          // Check if this is a credit from Google Sheets (these cannot be edited)
                          if (log.is_credit) {
                            return (
                              <Badge className="bg-red-600/90 text-white border-red-500 hover:bg-red-700">
                                <CreditCard size={14} className="mr-1.5" />
                                Credit
                              </Badge>
                            );
                          }
                          
                          // For all other cases, show editable dropdown
                          // Check if the selected result generates a credit for styling
                          const selectedOption = resultOptions.find(opt => opt.name === currentResult);
                          const generatesCredit = selectedOption?.generates_credit;
                          
                          return (
                            <Select
                              value={currentResult || "none"}
                              onValueChange={(value) => handleResultChange(callId, value === "none" ? "" : value)}
                            >
                              <SelectTrigger 
                                className={`h-8 text-xs transition-all duration-300 ${
                                  generatesCredit
                                    ? "bg-red-600/20 border-red-500/50 text-white"
                                    : currentResult 
                                    ? "bg-[#0d2a5a] border-[#1a3a6a] text-white" 
                                    : "bg-[#1a3a6a]/50 border-[#f4cc1e]/30 text-white/60 needs-attention"
                                }`}
                              >
                                <div className="flex items-center gap-1.5">
                                  {generatesCredit && <CreditCard size={14} className="text-red-400" />}
                                  <SelectValue placeholder="Select result" />
                                </div>
                              </SelectTrigger>
                              <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                                <SelectItem value="none" className="text-white/50">-- Select --</SelectItem>
                                {resultOptions.map((option) => (
                                  <SelectItem key={option.id} value={option.name} className="text-white">
                                    <div className="flex items-center gap-2">
                                      {option.name}
                                      {option.generates_credit && (
                                        <CreditCard size={12} className="text-red-400" />
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <p className="px-1 pt-3 text-xs text-white/40">
              <span className="font-semibold text-white/60">Valid Check-In:</span> "No" means the check-in didn't happen, was more than 500 ft from the property, or wasn't done within 3 hours of the scheduled appointment time.
            </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerDashboard;
